import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { StafferSettingsStoreService } from '@principle-theorem/ng-principle-shared';
import { IPopupNotification } from '@principle-theorem/notifications';
import {
  hardDeleteDoc,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { duration } from 'moment-timezone';
import { noop, Observable, of, ReplaySubject, timer } from 'rxjs';
import { concatMap, filter, map, take, tap } from 'rxjs/operators';
import { ContentViewComponent } from '../editor/content-view/content-view.component';
import { PopupNotificationsService } from '../popup-notifications.service';

@Component({
  selector: 'pr-popup-notification',
  templateUrl: './popup-notification.component.html',
  styleUrl: './popup-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ContentViewComponent,
    MatButtonModule,
    MatIcon,
    MatProgressSpinner,
    RouterLink,
    MatTooltip,
  ],
})
export class PopupNotificationComponent {
  notification$ = new ReplaySubject<WithRef<IPopupNotification>>(1);
  progress$: Observable<number | undefined>;
  isMuted$: Observable<boolean>;

  @Input()
  set notification(notification: WithRef<IPopupNotification>) {
    if (notification) {
      this.notification$.next(notification);
    }
  }

  constructor(
    private _snackBar: MatSnackBar,
    private _popupNotifications: PopupNotificationsService,
    private _stafferSettings: StafferSettingsStoreService
  ) {
    this.progress$ = this.notification$.pipe(
      concatMap((notification) => {
        if (!notification.hasTimeout) {
          return of(undefined);
        }

        const createdAt = moment();
        const intervalMs = duration(0.1, 'seconds').asMilliseconds();
        const totalTicks = Math.ceil(
          this._popupNotifications.timeoutMs / intervalMs
        );

        return timer(0, intervalMs).pipe(
          map(() => {
            const now = moment();
            const totalDuration = moment.duration(now.diff(createdAt));
            const progress =
              (totalDuration.asMilliseconds() /
                this._popupNotifications.timeoutMs) *
              100;
            return Math.min(progress, 100);
          }),
          take(totalTicks + 1)
        );
      })
    );

    this.isMuted$ = this._stafferSettings.notifications$.pipe(
      map((notifications) => notifications?.muteNotifications ?? false),
      shareReplayCold()
    );

    this.notification$
      .pipe(
        take(1),
        takeUntilDestroyed(),
        concatMap((notification) =>
          this._popupNotifications.playNotificationSound(notification)
        )
      )
      .subscribe(noop);

    this.notification$
      .pipe(
        filter((notification) => notification.hasTimeout),
        tap((notification) =>
          setTimeout(
            () => void this.removeNotification(notification),
            this._popupNotifications.timeoutMs
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe(noop);
  }

  async removeNotification(
    notification: WithRef<IPopupNotification>,
    withSnackbar = false
  ): Promise<void> {
    await hardDeleteDoc(notification.ref);
    if (withSnackbar) {
      this._snackBar.open(`Notification has been dismissed`);
    }
  }

  muteNotifications(muteNotifications: boolean): void {
    this._stafferSettings.updateStafferSettings({
      notifications: {
        muteNotifications,
      },
    });
  }
}
